import React, { useState, Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useHistory, Link } from 'react-router-dom';

import {
  selectToken,
  selectCurrentUser,
} from '../../../redux/user/userSelectors';
import { showAlert } from '../../../redux/alert/alertActions';
import { addPost } from '../../../redux/feed/feedActions';

import { createPost } from '../../../services/postService';
import { getCreatorProfile, getCreatorTiers } from '../../../services/monetizationService';
import { isProfane } from '../../../utils/profanity';

import Avatar from '@mui/material/Avatar';
import MobileHeader from '../../Header/MobileHeader/MobileHeader';
import Icon from '../../Icon/Icon';
import TextButton from '../../Button/TextButton/TextButton';
import Loader from '../../Loader/Loader';

const NewPostForm = ({
  token,
  file,
  previewImage,
  currentUser,
  hide,
  back,
  showAlert,
  addPost,
}) => {
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const [monetizationType, setMonetizationType] = useState('Free');
  const [selectedTierId, setSelectedTierId] = useState('');
  const [ppvPrice, setPpvPrice] = useState('4.99');
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [creatorTiers, setCreatorTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);

  const history = useHistory();

  useEffect(() => {
    if (!token) {
      return;
    }
    let cancelled = false;
    const loadCreatorSettings = async () => {
      setLoadingTiers(true);
      try {
        const [profileResponse, tiersResponse] = await Promise.all([
          getCreatorProfile(token).catch(() => null),
          getCreatorTiers(token, true).catch(() => []),
        ]);
        if (cancelled) {
          return;
        }
        if (profileResponse) {
          setCreatorProfile(profileResponse);
          if (profileResponse.defaultMonetizationType) {
            setMonetizationType(profileResponse.defaultMonetizationType);
          }
        }
        setCreatorTiers(tiersResponse || []);
      } catch (error) {
        console.warn('Unable to load creator subscription settings.', error);
      } finally {
        if (!cancelled) {
          setLoadingTiers(false);
        }
      }
    };
    loadCreatorSettings();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleClick = async (event) => {
    event.preventDefault();

    if (!file) {
      showAlert('Please select media to upload.');
      return;
    }

    if (caption && isProfane(caption)) {
      showAlert('Caption contains inappropriate language.');
      return;
    }

    if (monetizationType === 'SubscribersOnly' && !selectedTierId) {
      showAlert('Select which tier unlocks this post.');
      return;
    }

    let ppvPriceCents = null;
    if (monetizationType === 'PayPerView') {
      const parsed = Number.parseFloat(ppvPrice);
      if (Number.isNaN(parsed) || parsed <= 0) {
        showAlert('Enter a valid pay-per-view price.');
        return;
      }
      ppvPriceCents = Math.round(parsed * 100);
    }

    const formData = new FormData();
    const isVideo = file.type.startsWith('video/');
    formData.append(isVideo ? 'video' : 'images', file);
    formData.append('caption', caption);
    formData.append('crop', JSON.stringify(previewImage.crop || {}));
    formData.append('filter', !isVideo ? previewImage.filterName || '' : '');
    formData.append('MonetizationType', monetizationType);
    if (monetizationType === 'SubscribersOnly' && selectedTierId) {
      formData.append('SubscriptionTierId', selectedTierId);
    }
    if (ppvPriceCents) {
      formData.append('PayPerViewPriceCents', ppvPriceCents);
    }
  
    try {
      setLoading(true);
      const post = await createPost(formData, token);

      setLoading(false);
      hide();

      if (history.location.pathname === '/') {
        addPost(post); // Add to feed if already on home page
      } else {
        history.push('/'); // Navigate to home page
      }
    } catch (err) {
      setLoading(false);
      console.error('Error uploading post:', err);
      showAlert(err.message || 'Could not share post.', () => handleClick(event));
    }
  };

  if (!file) {
    return null;
  }

  return (
    <Fragment>
      {loading && <Loader />}
      <MobileHeader show>
        <Icon
          icon="chevron-back"
          onClick={back}
          style={{ cursor: 'pointer' }}
        />
        <h3 className="heading-3">New Post</h3>
        <TextButton
          bold
          blue
          style={{ fontSize: '1.5rem' }}
          onClick={handleClick}
        >
          Share
        </TextButton>
      </MobileHeader>
      <form className="new-post__form post-form">
        {previewImage?.src && (
          <div
            className={`post-form__preview ${
              previewImage.type === 'video' ? 'post-form__preview--video' : ''
            }`}
          >
            {previewImage.type === 'video' ? (
              <video
                src={previewImage.src}
                controls
                controlsList="nodownload"
                onContextMenu={(event) => event.preventDefault()}
                playsInline
                muted
                className="post-form__preview-media"
              />
            ) : (
              <img
                src={previewImage.src}
                alt="Preview"
                className="post-form__preview-media"
                style={{ filter: previewImage.filter || 'none' }}
              />
            )}
          </div>
        )}
        <div className="post-form__composer">
          <div className="post-form__input">
            <div className="post-form__avatar">
              <Avatar
                size="3rem"
                className="avatar--small"
                src={
                  currentUser.avatar ||
                  require('../../../assets/img/default-avatar.png').default
                }
              />
            </div>
            <textarea
              className="post-form__textarea"
              placeholder="Write a caption..."
              value={caption}
              onChange={(event) => setCaption(event.target.value)}
            />
          </div>
          {previewImage?.type === 'video' && (
            <p className="post-form__helper">
              Videos up to 60 seconds and max 200mb are supported.
            </p>
          )}
        </div>
        <div className="post-form__monetization">
          <h4 className="heading-4">Monetisation</h4>
          <label className="post-form__field">
            <span>Who can view this post?</span>
            <select
              value={monetizationType}
              onChange={(event) => setMonetizationType(event.target.value)}
            >
              <option value="Free">Everyone (free)</option>
              <option value="SubscribersOnly">Subscribers only</option>
              <option value="PayPerView">One-off unlock (PPV)</option>
            </select>
          </label>
          {monetizationType === 'SubscribersOnly' && (
            <label className="post-form__field">
              <span>
                Choose a tier{' '}
                {loadingTiers && <small className="color-light">loading…</small>}
              </span>
              {creatorTiers.length === 0 ? (
                <p className="heading-5 color-light">
                  You don't have any tiers yet.{' '}
                  <Link to="/creator/subscriptions">Create one in Creator Studio.</Link>
                </p>
              ) : (
                <select
                  value={selectedTierId}
                  onChange={(event) => setSelectedTierId(event.target.value)}
                >
                  <option value="">Select a tier</option>
                  {creatorTiers
                    .filter((tier) => !tier.isArchived)
                    .map((tier) => (
                      <option key={tier.tierId} value={tier.tierId}>
                        {tier.name} · ${(tier.monthlyPriceCents / 100).toFixed(2)}/mo
                      </option>
                    ))}
                </select>
              )}
            </label>
          )}
          {monetizationType === 'PayPerView' && (
            <label className="post-form__field">
              <span>Unlock price (AUD)</span>
              <input
                type="number"
                min="1"
                step="0.5"
                value={ppvPrice}
                onChange={(event) => setPpvPrice(event.target.value)}
              />
            </label>
          )}
        </div>
      </form>
    </Fragment>
  );
};

const mapStateToProps = createStructuredSelector({
  token: selectToken,
  currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
  showAlert: (text, onClick) => dispatch(showAlert(text, onClick)),
  addPost: (post) => dispatch(addPost(post)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewPostForm);
